<template>
  <div class="text-diff">
    <h1>文本比较工具</h1>
    
    <div class="diff-container">
      <!-- 输入区域 -->
      <div class="input-section">
        <div class="text-panel">
          <div class="panel-header">
            <h3>原始文本</h3>
            <div class="actions">
              <el-button size="small" @click="pasteOriginal">从剪贴板粘贴</el-button>
              <el-button size="small" @click="clearOriginal">清空</el-button>
            </div>
          </div>
          <el-input
            v-model="originalText"
            type="textarea"
            :rows="15"
            placeholder="请输入或粘贴原始文本"
            @input="handleCompare"
          />
        </div>

        <div class="text-panel">
          <div class="panel-header">
            <h3>比较文本</h3>
            <div class="actions">
              <el-button size="small" @click="pasteCompare">从剪贴板粘贴</el-button>
              <el-button size="small" @click="clearCompare">清空</el-button>
            </div>
          </div>
          <el-input
            v-model="compareText"
            type="textarea"
            :rows="15"
            placeholder="请输入或粘贴要比较的文本"
            @input="handleCompare"
          />
        </div>
      </div>

      <!-- 比较选项 -->
      <div class="options-section">
        <div class="option-group">
          <span>比较模式：</span>
          <el-radio-group v-model="options.mode" @change="handleCompare">
            <el-radio-button label="chars">逐字比较</el-radio-button>
            <el-radio-button label="words">单词比较</el-radio-button>
            <el-radio-button label="lines">逐行比较</el-radio-button>
          </el-radio-group>
        </div>

        <div class="option-group">
          <el-checkbox v-model="options.ignoreCase" @change="handleCompare">
            忽略大小写
          </el-checkbox>
          <el-checkbox v-model="options.ignoreWhitespace" @change="handleCompare">
            忽略空白字符
          </el-checkbox>
          <el-checkbox v-model="options.trimLines" @change="handleCompare">
            忽略行首尾空格
          </el-checkbox>
        </div>
      </div>

      <!-- 比较结果 -->
      <div class="result-section">
        <div class="section-header">
          <h3>比较结果</h3>
          <div class="diff-stats" v-if="diffStats.total > 0">
            <span class="stat-item">
              <span class="added">+{{ diffStats.added }}</span>
            </span>
            <span class="stat-item">
              <span class="removed">-{{ diffStats.removed }}</span>
            </span>
            <span class="stat-item">
              变更率: {{ diffStats.changeRate }}%
            </span>
          </div>
        </div>

        <div class="diff-view" v-if="diffResult.length > 0">
          <div
            v-for="(line, index) in diffResult"
            :key="index"
            class="diff-line"
            :class="line.type"
          >
            <span class="line-number">{{ line.lineNumber }}</span>
            <span class="line-content" v-html="line.content"></span>
          </div>
        </div>
        <div class="empty-result" v-else>
          <el-empty description="请输入要比较的文本" />
        </div>
      </div>

      <!-- 说明区域 -->
      <div class="info-section">
        <h3>使用说明</h3>
        <ul>
          <li>支持逐字、单词和逐行三种比较模式</li>
          <li>红色表示删除的内容，绿色表示新增的内容</li>
          <li>可选择是否忽略大小写和空白字符</li>
          <li>支持从剪贴板直接粘贴文本</li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed } from 'vue'
import { ElMessage } from 'element-plus'
import * as Diff from 'diff'

const originalText = ref('')
const compareText = ref('')
const diffResult = ref([])

const options = reactive({
  mode: 'chars',
  ignoreCase: false,
  ignoreWhitespace: false,
  trimLines: true
})

// 处理比较
const handleCompare = () => {
  if (!originalText.value && !compareText.value) {
    diffResult.value = []
    return
  }

  let text1 = originalText.value
  let text2 = compareText.value

  // 预处理文本
  if (options.ignoreCase) {
    text1 = text1.toLowerCase()
    text2 = text2.toLowerCase()
  }

  if (options.ignoreWhitespace) {
    text1 = text1.replace(/\s+/g, ' ')
    text2 = text2.replace(/\s+/g, ' ')
  }

  if (options.trimLines) {
    text1 = text1.split('\n').map(line => line.trim()).join('\n')
    text2 = text2.split('\n').map(line => line.trim()).join('\n')
  }

  // 执行比较
  let diff
  switch (options.mode) {
    case 'chars':
      diff = Diff.diffChars(text1, text2)
      break
    case 'words':
      diff = Diff.diffWords(text1, text2)
      break
    case 'lines':
      diff = Diff.diffLines(text1, text2)
      break
  }

  // 格式化结果
  formatDiffResult(diff)
}

// 格式化比较结果
const formatDiffResult = (diff) => {
  let lineNumber = 1
  let formattedResult = []

  diff.forEach(part => {
    const content = part.value
    const lines = content.split('\n')
    
    lines.forEach((line, index) => {
      if (index === lines.length - 1 && line === '') return

      let type = 'normal'
      if (part.added) type = 'added'
      if (part.removed) type = 'removed'

      formattedResult.push({
        lineNumber: type !== 'added' ? lineNumber++ : '',
        content: highlightDiff(line, type),
        type
      })
    })
  })

  diffResult.value = formattedResult
}

// 高亮差异
const highlightDiff = (text, type) => {
  if (type === 'normal') return text
  return `<span class="${type}">${text}</span>`
}

// 统计信息
const diffStats = computed(() => {
  const added = diffResult.value.filter(line => line.type === 'added').length
  const removed = diffResult.value.filter(line => line.type === 'removed').length
  const total = diffResult.value.length
  const changeRate = total > 0 ? 
    Math.round(((added + removed) / total) * 100) : 0

  return { added, removed, total, changeRate }
})

// 从剪贴板粘贴
const pasteOriginal = async () => {
  try {
    originalText.value = await navigator.clipboard.readText()
    handleCompare()
    ElMessage.success('粘贴成功')
  } catch (error) {
    ElMessage.error('无法访问剪贴板')
  }
}

const pasteCompare = async () => {
  try {
    compareText.value = await navigator.clipboard.readText()
    handleCompare()
    ElMessage.success('粘贴成功')
  } catch (error) {
    ElMessage.error('无法访问剪贴板')
  }
}

// 清空文本
const clearOriginal = () => {
  originalText.value = ''
  handleCompare()
}

const clearCompare = () => {
  compareText.value = ''
  handleCompare()
}
</script>

<style scoped>
.text-diff {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  margin-top: 80px;
}

.diff-container {
  margin-top: 20px;
}

.input-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin: 20px 0;
}

.text-panel {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.actions {
  display: flex;
  gap: 10px;
}

.options-section {
  margin: 20px 0;
  padding: 20px;
  background-color: #f5f7fa;
  border-radius: 8px;
}

.option-group {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 10px 0;
  flex-wrap: wrap;
}

.result-section {
  margin: 20px 0;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.diff-stats {
  display: flex;
  gap: 20px;
}

.stat-item {
  font-size: 14px;
}

.diff-view {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background-color: #fff;
  overflow-x: auto;
}

.diff-line {
  display: flex;
  padding: 2px 10px;
  font-family: monospace;
  white-space: pre;
}

.diff-line.added {
  background-color: #f0f9eb;
}

.diff-line.removed {
  background-color: #fef0f0;
}

.line-number {
  width: 40px;
  color: #909399;
  user-select: none;
}

.line-content {
  flex: 1;
}

.line-content :deep(.added) {
  background-color: #c2e7b0;
}

.line-content :deep(.removed) {
  background-color: #f9c0c0;
}

.empty-result {
  padding: 40px;
  text-align: center;
}

.info-section {
  margin-top: 30px;
  padding: 20px;
  background-color: #f5f7fa;
  border-radius: 8px;
}

.info-section ul {
  padding-left: 20px;
  margin: 10px 0;
}

.info-section li {
  margin: 8px 0;
  line-height: 1.5;
}

@media (max-width: 768px) {
  .input-section {
    grid-template-columns: 1fr;
  }
}
</style> 